import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BrandM,
  BrandS,
  BrandXS,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { MY_SORARE_TRANSACTIONS_ESSENCE } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { cardPull as cardPullMessage } from '@sorare/core/src/lib/glossary';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';
import { getShardColor } from '@sorare/core/src/set';
import type { HexColor } from '@sorare/core/src/style/types';

import { SBLine } from '#src/components/essence/CardPullExperience/CardPullInfos/ui.tsx';
import { Probability } from '#src/components/rewards/Probability/index.tsx';

import { EssenceBalance } from './EssenceBalance/index.tsx';
import type {
  EssenceFlavours_cardPull,
  LazyEssenceFlavoursQuery,
  LazyEssenceFlavoursQueryVariables,
} from './__generated__/index.graphql.ts';

const Percentage = styled(Horizontal)`
  gap: 0;
  align-items: center;
  justify-content: center;
  height: calc(6 * var(--unit));
  overflow: hidden;
  flex-wrap: wrap;
  /* auto hide overflowing logos */
  &:before {
    content: '';
    display: block;
    height: 100%;
  }
  > * {
    padding: 0 var(--unit);
  }
`;

const Percentages = styled(Horizontal)`
  gap: 2px;
  width: 100%;
  justify-content: center;
  align-items: stretch;
  border-radius: var(--unit);
  overflow: hidden;
  flex-direction: row-reverse;
`;

const Root = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
`;

const QUERY = gql`
  query LazyEssenceFlavoursQuery($id: ID!) {
    currentUser {
      slug
      cardShardsChest(id: $id) {
        id
        rarity
        cardShardsCount
        poolKeyShardsCounts {
          slug
          count
          displayName
          so5League {
            slug
            color(variant: PRIMARY)
            iconUrl
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  LazyEssenceFlavoursQuery,
  LazyEssenceFlavoursQueryVariables
>;

type Props = {
  id: string;
  rarity: Rarity;
  fullWidth: boolean;
  pill?: boolean;
  cardPull: Nullable<EssenceFlavours_cardPull>;
};

export const EssenceFlavours = ({
  id,
  rarity: defaultRarity,
  fullWidth,
  pill,
  cardPull,
}: Props) => {
  const {
    flags: { useEssenceBalance = false },
  } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  const { data, loading } = useQuery(QUERY, {
    variables: { id },
    skip: !open || !!cardPull,
  });

  const [tab, setTab] = useState<'odds' | 'balance'>('odds');

  const cardShardsChest = data?.currentUser?.cardShardsChest;

  const { poolKeyShardsCounts, rarity = defaultRarity } = (() => {
    if (cardPull) {
      return cardPull;
    }
    return (
      cardShardsChest || {
        poolKeyShardsCounts: [],
        rarity: undefined,
      }
    );
  })();

  const totalFlavourEssence = poolKeyShardsCounts.reduce(
    (acc, { count }) => acc + count,
    0
  );
  const sortedPoolKeyShardsCounts = sortBy(
    t => t.count,
    [...poolKeyShardsCounts]
  ).reverse();

  const cardShardsCount = cardShardsChest?.cardShardsCount;
  const anyRarityShardColor = getShardColor(null, null);

  return (
    <>
      {pill ? (
        <PillButton
          size="small"
          color="tertiary"
          startIcon={<Shards size={2} color={anyRarityShardColor} />}
          onClick={() => setOpen(true)}
        >
          <FormattedMessage {...cardPullMessage.myEssence} />
        </PillButton>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          startIcon={
            <img
              src={getRewardImageUrl('shard', rarity)}
              alt=""
              width={10}
              height={16}
            />
          }
          size="small"
          color="quaternary"
          fullWidth={fullWidth}
        >
          <FormattedMessage {...cardPullMessage.myEssence} />
        </Button>
      )}
      <Dialog
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Horizontal center gap={2}>
            <img
              src={getRewardImageUrl('shard', rarity)}
              alt=""
              width={20}
              height={32}
            />
            <BrandM className="text-center">
              <FormattedMessage {...cardPullMessage.myEssence} />
            </BrandM>
          </Horizontal>
        }
      >
        {!!useEssenceBalance && (
          <TabBar value={tab} fullWidth variant="flat" forceMobileLayout>
            <Tab value="odds" onClick={() => setTab('odds')}>
              <FormattedMessage defaultMessage="Odds" />
            </Tab>
            <Tab value="balance" onClick={() => setTab('balance')}>
              <FormattedMessage defaultMessage="Balance" />
            </Tab>
          </TabBar>
        )}

        <Root gap={2}>
          {tab === 'odds' && !!loading && <LoadingIndicator />}
          {tab === 'odds' && !loading && (
            <>
              <LabelM color="var(--c-nd-700)" className="text-center">
                <Vertical as="span">
                  {!!cardShardsCount && (
                    <FormattedMessage
                      defaultMessage="Your next draw odds are based on your total <bold>{nb} Essences</bold>"
                      values={{
                        nb: cardShardsChest?.cardShardsCount,
                        bold: (...token) => (
                          <BrandS bold color="var(--c-white)">
                            {token}
                          </BrandS>
                        ),
                      }}
                    />
                  )}
                  {!cardShardsCount && !!cardPull && (
                    <FormattedMessage
                      defaultMessage="Your current draw odds are based on:"
                      values={{
                        nb: cardShardsChest?.cardShardsCount,
                        bold: (...token) => (
                          <BrandS bold color="var(--c-white)">
                            {token}
                          </BrandS>
                        ),
                      }}
                    />
                  )}
                </Vertical>
              </LabelM>

              <Percentages>
                {sortedPoolKeyShardsCounts.map(item => {
                  const iconUrl = item.so5League?.iconUrl;

                  return (
                    <Percentage
                      key={item.slug}
                      style={{
                        width: `${(item.count / totalFlavourEssence) * 100}%`,
                        backgroundColor:
                          item.so5League?.color || 'var(--c-nd-600)',
                      }}
                    >
                      {!!iconUrl && (
                        <img src={iconUrl} alt={item.displayName} height={24} />
                      )}
                    </Percentage>
                  );
                })}
              </Percentages>
              <Vertical>
                {sortedPoolKeyShardsCounts.map(
                  ({ count, slug, displayName, so5League }) => {
                    return (
                      <SBLine key={slug}>
                        <Horizontal>
                          <Dot
                            size={1}
                            color={
                              (so5League?.color as HexColor | undefined) ||
                              'var(--c-nd-600)'
                            }
                          />
                          <BrandXS as="h3">{displayName}</BrandXS>
                        </Horizontal>
                        <Vertical style={{ alignItems: 'flex-end' }} gap={0.5}>
                          {!!totalFlavourEssence && (
                            <LabelM>
                              <Probability
                                probability={count / totalFlavourEssence}
                              />
                            </LabelM>
                          )}
                          <LabelS color="var(--c-nd-600)">
                            <Horizontal gap={0.5}>
                              <span>{count}</span>
                              <Shards size={1.5} />
                            </Horizontal>
                          </LabelS>
                        </Vertical>
                      </SBLine>
                    );
                  }
                )}
              </Vertical>

              <Button
                size="small"
                color="transparent"
                to={MY_SORARE_TRANSACTIONS_ESSENCE}
              >
                <FormattedMessage defaultMessage="See my transaction history" />
              </Button>
            </>
          )}
          {tab === 'balance' && <EssenceBalance rarity={rarity} />}
        </Root>
      </Dialog>
    </>
  );
};

EssenceFlavours.fragments = {
  cardPull: gql`
    fragment EssenceFlavours_cardPull on CardPull {
      id
      rarity
      poolKeyShardsCounts {
        slug
        count
        displayName
        so5League {
          slug
          color(variant: PRIMARY)
          iconUrl
        }
      }
    }
  ` as TypedDocumentNode<EssenceFlavours_cardPull>,
};
